//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { OverlayIconButton } from '@components/atoms/button/Button';
import { ContentCardRangeSelectContext } from '@components/organisms/content-card-grid/ContentCardRangeSelectContext';
import { useGetIsBulkSelected } from '@hooks/useGetIsBulkSelected';
import { useRemindMeLater } from '@hooks/useRemindMeLater';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import { UniversalItemType } from '@models/UniversalItemType';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import { IMediaTrackableComponent } from '@tracker/mediaTracking/mediaTrackingModel';
import { FeatureStorageScope } from '@utils/featureStorageModel';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface Props
  extends React.HTMLAttributes<HTMLDivElement>,
    Partial<ITrackableComponent>,
    IMediaTrackableComponent {
  itemType: UniversalItemType;
  id: ItemId;
  shouldShowHoverState?: boolean;
}

/**
 * The BulkActionSelect atom is a button that, when clicked, will
 * add the item matching the provided props to the bulk action
 * selections store. When clicked again, the item will be deselected.
 * The internal state is dictated by the store itself, so actions which
 * add or remove items from the store will affect instances of this atom
 * everywhere.
 */
const BulkActionSelect = ({
  className,
  id,
  itemType,
  mediaTrackingConfig,
  shouldShowHoverState,
  trackingId,
}: Props) => {
  const dispatch = useDispatch();

  const [wasRangeSelectToastShownToday, hideRangeSelectToast] =
    useRemindMeLater(FeatureStorageScope.STARTED_BULK_SELECTION);

  const isSelected = useGetIsBulkSelected(itemType, id);

  const { lastSelectedItem, orderedPartialCuids, setLastSelectedItem } =
    useContext(ContentCardRangeSelectContext);

  const cuid = PartialCuid.build({ itemType, id });
  const currentItemIndex = orderedPartialCuids.indexOf(cuid);

  const toggleSelection = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const isShiftPressed = event.shiftKey;
    if (isSelected) {
      dispatch(
        getBulkActionsActions().unselectItems({
          currentItemIndex,
          orderedPartialCuids,
          lastSelectedItem,
          cuid,
          isShiftPressed,
          setLastSelectedItem,
        }),
      );
    } else {
      dispatch(
        getBulkActionsActions().selectItems({
          currentItemIndex,
          orderedPartialCuids,
          lastSelectedItem,
          cuid,
          isShiftPressed,
          setLastSelectedItem,
          wasRangeSelectToastShownToday,
          hideRangeSelectToast,
          source: mediaTrackingConfig?.source,
        }),
      );
    }
  };

  return (
    <BulkSelectContainer className={className} trackingId={trackingId}>
      <LargeOverlayIconButton
        iconName='checkmark'
        isSelected={isSelected}
        onClick={toggleSelection}
        shouldShowHoverState={shouldShowHoverState}
      />
    </BulkSelectContainer>
  );
};

const BulkSelectContainer = withTracking(styled.div``);

const LargeOverlayIconButton = styled(OverlayIconButton)<{
  isSelected: boolean;
  shouldShowHoverState?: boolean;
}>(
  ({ isSelected, shouldShowHoverState, theme }) => `
  && > button {
    padding: ${theme.spacings[1]} ${theme.spacings[1]} ${
      theme.spacings[1]
    } calc(${theme.spacings[1]} + 2px);
    ${
      isSelected
        ? `
      background-color: ${theme.colors.aliases.primaryShade2};
      border-color: ${theme.colors.aliases.primaryShade2};
    `
        : ''
    };
    ${
      shouldShowHoverState
        ? `
      background-color: ${theme.colors.aliases.primaryShade1};
      border-color: ${theme.colors.aliases.primaryShade1};
    `
        : ''
    };
    svg {
      height: ${theme.spacings[4]};
      width: calc(${theme.spacings[4]} - 2px);
    }
`,
);

export default BulkActionSelect;
